import React, { useState, useEffect, useRef } from 'react'

import { getRotationDegrees, getStartingRotationDegrees } from './utils'
import rouletteSelector from 'images/marker.svg'
import {
  RouletteContainer,
  RouletteSelectorImage,
  RotationContainer,
} from './styles'
import {
  DEFAULT_BACKGROUND_COLORS,
  DEFAULT_TEXT_COLORS,
  DEFAULT_OUTER_BORDER_COLOR,
  DEFAULT_OUTER_BORDER_WIDTH,
  DEFAULT_INNER_RADIUS,
  DEFAULT_INNER_BORDER_COLOR,
  DEFAULT_INNER_BORDER_WIDTH,
  DEFAULT_RADIUS_LINE_COLOR,
  DEFAULT_RADIUS_LINE_WIDTH,
  DEFAULT_FONT_SIZE,
  DEFAULT_TEXT_DISTANCE,
  DEFAULT_SPIN_DURATION,
  DEFAULT_BACKGROUND_COLOR,
} from './strings'
import WheelCanvas from './WheelCanvas'
import analytics from 'global/AnalyticsData'
import { getUserType } from 'global/context/sub-contexts/userContext'
import { userTypeEnum } from 'services/user'

const STARTED_SPINNING = 'started-spinning'

const START_SPINNING_TIME = 2600
const CONTINUE_SPINNING_TIME = 750
const STOP_SPINNING_TIME = 8000

/*
args necessary: data.
example: fakeWheelData = [
  { option: '0', style: { backgroundColor: 'green', textColor: 'black' } },
}
*/
const Wheel = ({
  mustStartSpinning,
  startingOptionIndex = 0,
  prizeNumber,
  data = [{ id: 0, option: '' }],
  onStopSpinning = () => null,
  backgroundColors = DEFAULT_BACKGROUND_COLORS,
  textColors = DEFAULT_TEXT_COLORS,
  outerBorderColor = DEFAULT_OUTER_BORDER_COLOR,
  outerBorderWidth = DEFAULT_OUTER_BORDER_WIDTH,
  innerRadius = DEFAULT_INNER_RADIUS,
  innerBorderColor = DEFAULT_INNER_BORDER_COLOR,
  innerBorderWidth = DEFAULT_INNER_BORDER_WIDTH,
  radiusLineColor = DEFAULT_RADIUS_LINE_COLOR,
  radiusLineWidth = DEFAULT_RADIUS_LINE_WIDTH,
  fontSize = DEFAULT_FONT_SIZE,
  perpendicularText = false,
  textDistance = DEFAULT_TEXT_DISTANCE,
  spinDuration = DEFAULT_SPIN_DURATION,
  backgroundColor = DEFAULT_BACKGROUND_COLOR,
  sliceBorderColor = null,
  sliceBorderWidth = 0,
}) => {




  const isGuest = getUserType() === userTypeEnum.GUEST
  const [wheelData, setWheelData] = useState([...data])
  const [startRotationDegrees, setStartRotationDegrees] = useState(
    getStartingRotationDegrees(startingOptionIndex, data.length),
  )
  const [finalRotationDegrees, setFinalRotationDegrees] = useState(0)
  const [hasStartedSpinning, setHasStartedSpinning] = useState(false)
  const [hasStoppedSpinning, setHasStoppedSpinning] = useState(false)
  const [isCurrentlySpinning, setIsCurrentlySpinning] = useState(false)
  const [isDataUpdated, setIsDataUpdated] = useState(false)

  const normalizedSpinDuration = Math.max(0.01, spinDuration)

  const startSpinningTime = START_SPINNING_TIME * normalizedSpinDuration
  const continueSpinningTime = CONTINUE_SPINNING_TIME * normalizedSpinDuration
  const stopSpinningTime = STOP_SPINNING_TIME * normalizedSpinDuration

  const totalSpinningTime =
    startSpinningTime + continueSpinningTime + stopSpinningTime

  const mustStopSpinning = useRef(false)

  useEffect(() => {
    const dataLength = data.length
    const wheelDataAux = [{ option: '' }]
    for (let i = 0; i < dataLength; i++) {
      wheelDataAux[i] = {
        ...data[i],
        style: {
          backgroundColor:
            data[i].style?.backgroundColor ||
            backgroundColors[i % backgroundColors.length],
          textColor:
            data[i].style?.textColor || textColors[i % textColors.length],
          fontSize: data[i].style?.fontSize || null,
        },
      }
    }
    setWheelData([...wheelDataAux])
    setIsDataUpdated(true)
  }, [data, backgroundColors, textColors])

  useEffect(() => {
    if (mustStartSpinning && !isCurrentlySpinning) {
      setIsCurrentlySpinning(true)
      startSpinning()
      analytics.stepEvent('instant_win_spin_click', {}, { isGuest })
      const finalRotationDegreesCalculated = getRotationDegrees(
        prizeNumber,
        data.length,
      )
      setFinalRotationDegrees(finalRotationDegreesCalculated)
    }
  }, [mustStartSpinning])

  useEffect(() => {
    if (hasStoppedSpinning) {
      setIsCurrentlySpinning(false)
      setStartRotationDegrees(finalRotationDegrees)
    }
  }, [hasStoppedSpinning])

  const startSpinning = () => {
    setHasStartedSpinning(true)
    setHasStoppedSpinning(false)
    mustStopSpinning.current = true
    setTimeout(() => {
      if (mustStopSpinning.current) {
        mustStopSpinning.current = false
        setHasStartedSpinning(false)
        setHasStoppedSpinning(true)
        onStopSpinning()
      }
    }, totalSpinningTime)
  }

  const getRouletteClass = () => {
    if (hasStartedSpinning) {
      return STARTED_SPINNING
    }
    return ''
  }

  if (!isDataUpdated) {
    return null
  }


  return (
    <RouletteContainer>
      <RotationContainer
        className={getRouletteClass()}
        startSpinningTime={startSpinningTime}
        continueSpinningTime={continueSpinningTime}
        stopSpinningTime={stopSpinningTime}
        startRotationDegrees={startRotationDegrees}
        finalRotationDegrees={finalRotationDegrees}
      >
        <WheelCanvas
          width="900"
          height="900"
          data={wheelData}
          outerBorderColor={outerBorderColor}
          outerBorderWidth={outerBorderWidth}
          innerRadius={innerRadius}
          innerBorderColor={innerBorderColor}
          innerBorderWidth={innerBorderWidth}
          radiusLineColor={radiusLineColor}
          radiusLineWidth={radiusLineWidth}
          fontSize={fontSize}
          perpendicularText={perpendicularText}
          textDistance={textDistance}
          backgroundColor={backgroundColor}
          sliceBorderColor={sliceBorderColor}
          sliceBorderWidth={sliceBorderWidth}
        />
      </RotationContainer>
      <RouletteSelectorImage src={rouletteSelector} alt="roulette-static" />
    </RouletteContainer>
  )
}

export default Wheel
